import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import {
    PurchasingLabelStatisticsExportItem,
    StatsModel,
} from "@/services/api/moreFeatures/purchasingLabel/purchasingLabelStatistics";
import to from "@/utils/to";
import { message } from "antd";
import delay from "@/utils/delay";
import PurchasingLabelStatisticsProductTable from "./PurchasingLabelStatisticsProductTable";

export function getShowPurchasingLabelStatisticsExportItem(exportItems: PurchasingLabelStatisticsExportItem[]) {
    const showProductPicture = exportItems.includes(PurchasingLabelStatisticsExportItem.ITEM_IMAGE);
    const showProductTitle = exportItems.includes(PurchasingLabelStatisticsExportItem.ITEM_TITLE);
    const showProductExternalId = exportItems.includes(PurchasingLabelStatisticsExportItem.SELLER_CODE);
    const showSkuPicture = exportItems.includes(PurchasingLabelStatisticsExportItem.SIMPLE_IMAGE);
    const showSkuProperty = exportItems.includes(PurchasingLabelStatisticsExportItem.SIMPLE_PROPERTY);
    const showSkuExternalId = exportItems.includes(PurchasingLabelStatisticsExportItem.SIMPLE_SELLER_CODE);
    const showSkuPrice = exportItems.includes(PurchasingLabelStatisticsExportItem.SIMPLE_PRICE);
    const showSkuQuantity = exportItems.includes(PurchasingLabelStatisticsExportItem.QUANTITY);
    const showTotal = exportItems.includes(PurchasingLabelStatisticsExportItem.TOTAL);
    /** 单独一列 */
    const showSellerRemark = exportItems.includes(PurchasingLabelStatisticsExportItem.SELLER_REMARK);
    const showBuyerRemark = exportItems.includes(PurchasingLabelStatisticsExportItem.BUYER_REMARK);

    const shownItemLength = [
        showProductPicture,
        showProductTitle,
        showProductExternalId,
        showSkuPicture,
        showSkuProperty,
        showSkuExternalId,
        showSkuPrice,
        showSkuQuantity,
        showTotal,
    ].filter(Boolean).length;

    return {
        showProductPicture,
        showProductTitle,
        showProductExternalId,
        showSkuPicture,
        showSkuProperty,
        showSkuExternalId,
        showSkuPrice,
        showSkuQuantity,
        showTotal,
        showSellerRemark,
        showBuyerRemark,
        shownItemLength,
    };
}

interface PrintPurchasingLabelExcelParams {
    statsModels: StatsModel[];
    onProcess(loading: boolean): void;
    exportItems: PurchasingLabelStatisticsExportItem[];
    text: {
        range: string;
        exportTime: string;
    };
}

function addDomToWindow(): [HTMLIFrameElement, HTMLDivElement] {
    const iframe = document.createElement("iframe");

    iframe.style.width = "100%";

    const originStyle = [];

    Array.from(document.head.childNodes).forEach((el) => {
        /** 本地开发环境 */
        if (import.meta.env.DEV || import.meta.env.VITE_IS_QN) {
            if (el.nodeName === "STYLE") {
                const clone = el.cloneNode(true);

                originStyle.push(clone);
            }
        } else {
            if (el.nodeName === "LINK" && (el as HTMLLinkElement).rel === "stylesheet") {
                const clone = el.cloneNode(true);

                originStyle.push(clone);
            }
        }
    });

    /** @see https://blog.csdn.net/weixin_40579884/article/details/115617209 */
    if (navigator.userAgent.match("Firefox")) {
        iframe.src = "javascript:";
    }

    document.body.appendChild(iframe);

    const div = document.createElement("div");

    iframe.contentWindow.document.body.appendChild(div);

    iframe.contentWindow.document.head.append(...originStyle);

    return [iframe, div];
}

/** 等待所有照片加载完成才开始打印,避免打开打印窗口图片为空 */
async function waitAllImagesLoad(imageList: HTMLImageElement[]) {
    return Promise.all(
        imageList.map(
            (img) =>
                new Promise((resolve, reject) => {
                    if (img.complete) {
                        resolve(img);

                        return;
                    }

                    img.onload = () => {
                        resolve(img);
                    };

                    img.onerror = () => {
                        reject(new Error("图片加载失败"));
                    };
                }),
        ),
    );
}

export function printPurchasingLabelExcel({
    statsModels,
    onProcess,
    exportItems,
    text,
}: PrintPurchasingLabelExcelParams) {
    const [iframe, div] = addDomToWindow();
    const root = createRoot(div);

    const onRender = async () => {
        onProcess(true);

        await delay(100);

        const images = iframe.contentWindow.document.querySelectorAll("img");

        const imageList = Array.from(images);

        const [error] = await to(waitAllImagesLoad(imageList));

        if (error) {
            message.error(`打印失败:${error?.message}`);
        }

        iframe.contentWindow.print();

        root.unmount();

        document.body.removeChild(iframe);

        onProcess(false);
    };

    const PrintContent: React.FC = () => {
        useEffect(() => {
            onRender();
        }, []);

        return (
            <>
                <div style={{ paddingBottom: "5px" }}>
                    支付时间范围:{text.range};备货单 导出时间 {text.exportTime}
                </div>
                <PurchasingLabelStatisticsProductTable
                    statsModels={statsModels}
                    exportItems={exportItems}
                    className="print"
                />
            </>
        );
    };

    root.render(<PrintContent />);
}
